import {
  Autocomplete,
  AutocompleteOption,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  getAppointmentDate,
  getAppointmentTimeFrom,
  getAppointmentTimeUntil,
} from '../../../api/created/appointment.ts';
import { useGetAppointmentsForPatientWithoutLabOrder } from '../../../api/generated/appointment-controller/appointment-controller.ts';
import { AppointmentDto } from '../../../api/generated/model';
import { DashedButton } from '../../../components/DashedButtonStyled.tsx';
import { getAppointmentTypeName } from '../../../utils/appointment.ts';
import { formatDateWithOptionalTime } from '../../../utils/date.ts';
import { CreateLabOrderResultDialog } from './CreateLabOrderResultDialog.tsx';

interface FormValues {
  appointment: AppointmentDto | null;
}

export function SelectAppointmentForLabOrderResultDialog({
  open,
  onClose,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
}) {
  const { data: appointments, isPending: isAppointmentsPending } =
    useGetAppointmentsForPatientWithoutLabOrder(patientId);

  const [isCreateTestResultDialogOpen, setIsCreateTestResultDialogOpen] = useState(false);
  const { control, reset, watch, setValue } = useForm<FormValues>({ defaultValues: { appointment: null } });

  const appointmentValue = watch('appointment');

  useEffect(() => {
    if (open) {
      reset({
        appointment: null,
      });
    }
  }, [open]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleOnClose}>
        <ModalDialog sx={{ width: 500 }}>
          <ModalClose />
          <Typography level={'h4'}>Testresultat erfassen</Typography>
          <Divider />
          <FormControl>
            <FormLabel>
              <Typography>Termin</Typography>
            </FormLabel>

            <Controller
              name={'appointment'}
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={appointments ?? []}
                  loading={isAppointmentsPending}
                  getOptionLabel={(option) => formatAppointmentSelection(option)}
                  onChange={(_, item) => onChange(item)}
                  value={value}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props} key={option.id}>
                      {formatAppointmentSelection(option)}
                    </AutocompleteOption>
                  )}
                />
              )}
            />
          </FormControl>
          <DashedButton
            onClick={() => {
              setValue('appointment', null);
              setIsCreateTestResultDialogOpen(true);
            }}>
            Anderes Resultat eintragen
          </DashedButton>
          <Stack paddingTop={2} direction={'row'} justifyContent={'flex-end'} gap={1}>
            <Button
              color={'neutral'}
              onClick={() => {
                handleOnClose();
              }}>
              Abbrechen
            </Button>
            <Button disabled={!appointmentValue} onClick={() => setIsCreateTestResultDialogOpen(true)}>
              Weiter
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
      <CreateLabOrderResultDialog
        open={isCreateTestResultDialogOpen}
        onClose={() => {
          setIsCreateTestResultDialogOpen(false);
          handleOnClose();
        }}
        appointment={appointmentValue}
        patientId={patientId}
      />
    </>
  );
}

const formatAppointmentSelection = (appointment: AppointmentDto) =>
  `${formatDateWithOptionalTime(getAppointmentDate(appointment) ?? '', getAppointmentTimeFrom(appointment))}${
    getAppointmentTimeUntil(appointment) ? ` - ${getAppointmentTimeUntil(appointment)}` : ''
  } (${getAppointmentTypeName(appointment.type)})`;
